import { Recipe, ScrapedProduct, StoreId } from './types';

export type ProductFeedResult = {
  products: ScrapedProduct[];
  warnings: string[];
  provider: string;
  cache?: 'hit' | 'miss';
  fetchedAt?: string;
};

const defaultTimeoutMs = 4500;

function collectIngredientIds(recipes: Recipe[]) {
  return Array.from(new Set(
    recipes.flatMap((recipe) => recipe.ingredients.map((ingredient) => ingredient.ingredientId))
  )).sort();
}

export async function fetchMealPlannerProducts(
  storeId: StoreId,
  recipes: Recipe[],
  signal?: AbortSignal
): Promise<ProductFeedResult> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), defaultTimeoutMs);
  const abortListener = () => controller.abort();

  signal?.addEventListener('abort', abortListener, { once: true });

  const apiBaseUrl = process.env.REACT_APP_MEAL_PLANNER_API_BASE_URL || '';
  const ingredientIds = collectIngredientIds(recipes);
  const params = new URLSearchParams({
    storeId,
    ingredientIds: ingredientIds.join(','),
  });

  try {
    const response = await fetch(`${apiBaseUrl}/api/meal-planner/products?${params.toString()}`, {
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Product feed returned ${response.status}`);
    }

    const payload = await response.json() as Partial<ProductFeedResult>;

    return {
      products: Array.isArray(payload.products) ? payload.products : [],
      warnings: Array.isArray(payload.warnings) ? payload.warnings : [],
      provider: payload.provider || 'unknown',
      cache: payload.cache,
      fetchedAt: payload.fetchedAt,
    };
  } finally {
    window.clearTimeout(timeout);
    signal?.removeEventListener('abort', abortListener);
  }
}
